import { Globe, BarChart3, ShieldCheck, Wallet, Rocket, Music2 } from "lucide-react";
import { Reveal } from "./anim";

const VINYL_IMG =
  "https://images.unsplash.com/photo-1776064140852-909014591d2a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHx2aW55bCUyMHJlY29yZCUyMHNwb3RsaWdodCUyMG1pbmltYWx8ZW58MHx8fHwxNzg1NDcyMjUyfDA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Globe, title: "Global delivery", desc: "One upload reaches Spotify, Apple Music, Tidal, Amazon and 150+ stores worldwide." },
  { icon: Wallet, title: "Keep 100% royalties", desc: "No commission. Transparent payouts straight to your account, every cycle." },
  { icon: BarChart3, title: "Real-time analytics", desc: "Track streams, saves and listener geography as the numbers roll in." },
  { icon: ShieldCheck, title: "Content protection", desc: "Automatic Content ID and takedown handling keep your catalog yours." },
];

// Real partner logos, pre-processed to white on transparent background.
const LOGO_CLASS = "h-12 md:h-14 w-auto opacity-90 hover:opacity-100 transition-opacity object-contain";

const UMGLogo = () => (
  <img
    src="/logos/umg.png"
    alt="Universal Music Group"
    className={LOGO_CLASS}
    loading="lazy"
  />
);

const VirginLogo = () => (
  <img
    src="/logos/virgin.png"
    alt="Virgin Music Group"
    className={LOGO_CLASS}
    loading="lazy"
  />
);

export default function Features() {
  return (
    <section id="features" className="py-24 sm:py-32" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <Reveal>
            <span className="text-xs tracking-[0.25em] uppercase font-bold text-slate-400">The toolkit</span>
            <h2 className="mt-4 font-display font-medium tracking-tight text-white text-3xl sm:text-4xl md:text-5xl max-w-2xl">
              Everything you need to release like a label.
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl">
              Delivered worldwide through <span className="text-white">Virgin Music Group</span> (a Universal Music Group company) and 150+ platforms.
            </p>
          </Reveal>
        </div>

        {/* Partner logos */}
        <Reveal>
          <div
            className="mb-14 rounded-2xl border border-white/10 bg-white/[0.02] px-6 md:px-10 py-6 md:py-7 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
            data-testid="partners-strip"
          >
            <div className="text-[11px] tracking-[0.3em] uppercase font-semibold text-slate-500 shrink-0">
              Distributed in partnership with
            </div>
            <div className="flex items-center gap-10 md:gap-14 text-slate-300 flex-wrap">
              <div className="flex items-center" data-testid="partner-umg" title="Universal Music Group">
                <UMGLogo />
              </div>
              <div className="hidden md:block h-8 w-px bg-white/10" />
              <div className="flex items-center" data-testid="partner-virgin" title="Virgin Music Group">
                <VirginLogo />
              </div>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Large bento cell with imagery */}
          <Reveal className="md:col-span-2 md:row-span-2">
            <div className="relative h-full min-h-[320px] rounded-3xl overflow-hidden border border-white/10 group">
              <img src={VINYL_IMG} alt="Vinyl record" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 group-hover:opacity-70 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/50 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8">
                <Rocket className="text-blue-400 mb-4" size={28} />
                <h3 className="font-display text-2xl md:text-3xl text-white tracking-tight">Ship a release in minutes</h3>
                <p className="mt-3 text-slate-300 max-w-md leading-relaxed">
                  Drag in your master, set the metadata, pick a release date. Our team reviews every drop before it goes live.
                </p>
              </div>
            </div>
          </Reveal>

          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.05}>
              <div className="h-full rounded-3xl border border-white/10 bg-white/[0.02] p-7 hover:bg-white/[0.05] transition-colors">
                <f.icon className="text-blue-400 mb-5" size={26} />
                <h3 className="font-display text-xl text-white tracking-tight">{f.title}</h3>
                <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-white/10 bg-blue-600/10 p-7 flex flex-col justify-between">
              <Music2 className="text-blue-400" size={26} />
              <div>
                <h3 className="font-display text-xl text-white tracking-tight">Built for catalogs</h3>
                <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">
                  Manage artists, splits and multiple projects from a single label workspace.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
